import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Check, ShieldCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { getSunSign, getSignName, getSignEmoji, ZODIAC_SIGNS } from '../lib/astrology';
import { Spinner } from '../components/ui/Spinner';
import { CityAutocomplete } from '../components/ui/CityAutocomplete';
import { BirthDateInput } from '../components/ui/BirthDateInput';
import { BirthTimeInput } from '../components/ui/BirthTimeInput';
import { useI18n } from '../hooks/useI18n';
import { safeInvoke } from '../lib/safeInvoke';
import { validateAge, getAgeValidationMessage, logAgeValidation } from '../utils/ageValidation';

// ─── Progress dots ────────────────────────────────────────────────────
function ProgressDots({ step, total }: { step: number; total: number }) {
  const { t } = useI18n();
  return (
    <div className="step-dots" aria-label={t('pages.onboarding.stepOf').replace('{step}', String(step + 1)).replace('{total}', String(total))}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`step-dot ${i === step ? 'active' : i < step ? 'completed' : ''}`}
        />
      ))}
    </div>
  );
}

// ─── Step 0: Welcome ──────────────────────────────────────────────────
function StepWelcome({ name, onNext }: { name: string; onNext: () => void }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>✦</span>
      <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem, 5vw, 1.75rem)', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
        Em 90 segundos você vai ter sua primeira leitura real{name ? `, ${name}` : ''}.
      </h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: 1.7 }}>
        Não é horóscopo. É o seu mapa astral calculado de verdade.
      </p>

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 2rem', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        {[
          { icon: '🎯', text: 'Mapa natal calculado com base no céu real' },
          { icon: '✍️', text: 'Leitura escrita pra você, não copiada' },
          { icon: '🎁', text: 'Funciona grátis, sem cartão' },
        ].map(({ icon, text }) => (
          <li key={text} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
            <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>{icon}</span>
            {text}
          </li>
        ))}
      </ul>

      <button className="btn btn-primary btn-lg btn-block" onClick={onNext}>
        Começar
      </button>
    </div>
  );
}

// ─── Step 1: Birth data ───────────────────────────────────────────────
interface BirthData {
  birth_date: string;
  birth_time: string;
  birth_city: string;
}

function StepBirthData({
  onNext,
  onBack,
}: {
  onNext: (data: BirthData, sunSign: string) => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const { profile } = useAuth();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [city, setCity] = useState('');
  const [detectedSign, setDetectedSign] = useState('');
  const [ageError, setAgeError] = useState<string | null>(null);

  function handleDateChange(v: string) {
    setDate(v);
    setAgeError(null);
    if (v) {
      const key = getSunSign(v);
      setDetectedSign(key);
    } else {
      setDetectedSign('');
    }
  }

  function handleNext() {
    if (!date) return;
    const result = validateAge(date);
    if (!result.isValid) {
      setAgeError(getAgeValidationMessage(result.reason));
      logAgeValidation({ userId: profile?.id ?? null, birthDate: date, age: result.age, result: `blocked_${result.reason}` as never, context: 'natal_chart_setup' });
      return;
    }
    logAgeValidation({ userId: profile?.id ?? null, birthDate: date, age: result.age, result: 'passed', context: 'natal_chart_setup' });
    onNext({ birth_date: date, birth_time: time, birth_city: city }, detectedSign);
  }

  const sign = ZODIAC_SIGNS.find(s => s.key === detectedSign);

  return (
    <div>
      <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '1rem', textAlign: 'center' }}>🌙</span>
      <h2 className="font-display" style={{ fontSize: '1.375rem', color: 'var(--text-primary)', marginBottom: '0.5rem', textAlign: 'center' }}>
        {t('pages.onboarding.birthDataTitle')}
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', textAlign: 'center', fontSize: 'var(--text-label)' }}>
        {t('pages.onboarding.birthDataDesc')}
      </p>

      <div className="form-group">
        <label className="form-label">{t('pages.onboarding.birthDateLabel')}</label>
        <BirthDateInput value={date} onChange={v => handleDateChange(v)} required />
        {ageError && (
          <p style={{ color: '#fca5a5', fontSize: 'var(--text-caption)', marginTop: '0.35rem' }}>{ageError}</p>
        )}
      </div>

      {/* Animated sign badge appears as soon as date is filled */}
      {sign && (
        <div className="animate-fade-in" style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
          <span className="sign-badge" style={{ fontSize: '0.9375rem', padding: '0.4rem 1rem' }}>
            {sign.emoji} {t('pages.onboarding.youAreSign').replace('{sign}', sign.name)}
          </span>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">
          {t('pages.onboarding.birthTimeLabel')} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{t('pages.onboarding.optional')}</span>
        </label>
        <BirthTimeInput value={time} onChange={setTime} />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="birth_city">
          {t('pages.onboarding.birthCityLabel')} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{t('pages.onboarding.optional')}</span>
        </label>
        <CityAutocomplete
          id="birth_city"
          value={city}
          onChange={setCity}
          placeholder="Ex: São Paulo, Brasil"
        />
      </div>

      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 1rem', textAlign: 'center' }}>
        🔒 Seus dados ficam só com você. LGPD.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
        <button className="btn btn-secondary" onClick={onBack} style={{ flex: '0 0 auto' }}>
          {t('pages.onboarding.backBtn')}
        </button>
        <button
          className="btn btn-primary"
          style={{ flex: 1 }}
          onClick={handleNext}
          disabled={!date}
        >
          {t('pages.onboarding.continueBtn')} <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 2: Personalisation quiz ────────────────────────────────────
interface QuizData {
  purpose:    string;
  focus_area: string;
  experience: string;
}

const PURPOSE_OPTIONS = [
  { value: 'self_knowledge',  label: '🔍 Autoconhecimento' },
  { value: 'love',            label: '💕 Vida amorosa' },
  { value: 'career',          label: '💼 Carreira e propósito' },
  { value: 'spirituality',    label: '🌟 Espiritualidade' },
  { value: 'decisions',       label: '🧭 Tomar decisões' },
];

const FOCUS_OPTIONS = [
  { value: 'love',            label: '❤️ Amor' },
  { value: 'career',          label: '🚀 Carreira' },
  { value: 'health',          label: '🌿 Saúde' },
  { value: 'self_knowledge',  label: '✨ Autoconhecimento' },
];

const EXPERIENCE_OPTIONS = [
  { value: 'beginner',       label: 'Iniciante, estou começando agora' },
  { value: 'intermediate',   label: 'Intermediário, já conheço o básico' },
  { value: 'advanced',       label: 'Avançado, estudo astrologia a fundo' },
];

function StepQuiz({ onNext, onBack }: { onNext: (data: QuizData) => void; onBack: () => void }) {
  const [purpose,    setPurpose]    = useState('');
  const [focusArea,  setFocusArea]  = useState('');
  const [experience, setExperience] = useState('');

  const canContinue = purpose && focusArea && experience;

  return (
    <div>
      <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '1rem', textAlign: 'center' }}>🔮</span>
      <h2 className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: '0.25rem', textAlign: 'center' }}>
        Vamos personalizar sua experiência
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', textAlign: 'center', fontSize: 'var(--text-label)' }}>
        Apenas 3 perguntas rápidas.
      </p>

      {/* Purpose */}
      <div className="form-group">
        <label className="form-label">O que você busca na astrologia?</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {PURPOSE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPurpose(opt.value)}
              style={{
                textAlign: 'left',
                padding: '0.5rem 0.875rem',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${purpose === opt.value ? 'var(--petrol)' : 'var(--border-subtle)'}`,
                backgroundColor: purpose === opt.value ? 'rgba(31,78,95,0.08)' : 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-label)',
                cursor: 'pointer',
                fontWeight: purpose === opt.value ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Focus area */}
      <div className="form-group">
        <label className="form-label">Qual área da vida é sua prioridade agora?</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
          {FOCUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setFocusArea(opt.value)}
              style={{
                padding: '0.5rem 0.5rem',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${focusArea === opt.value ? 'var(--petrol)' : 'var(--border-subtle)'}`,
                backgroundColor: focusArea === opt.value ? 'rgba(31,78,95,0.08)' : 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-label)',
                cursor: 'pointer',
                fontWeight: focusArea === opt.value ? 600 : 400,
                textAlign: 'center',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Experience */}
      <div className="form-group">
        <label className="form-label">Qual é sua experiência com astrologia?</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {EXPERIENCE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setExperience(opt.value)}
              style={{
                textAlign: 'left',
                padding: '0.5rem 0.875rem',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${experience === opt.value ? 'var(--petrol)' : 'var(--border-subtle)'}`,
                backgroundColor: experience === opt.value ? 'rgba(31,78,95,0.08)' : 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-label)',
                cursor: 'pointer',
                fontWeight: experience === opt.value ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
        <button className="btn btn-secondary" onClick={onBack} style={{ flex: '0 0 auto' }}>
          Voltar
        </button>
        <button
          className="btn btn-primary"
          style={{ flex: 1 }}
          onClick={() => onNext({ purpose, focus_area: focusArea, experience })}
          disabled={!canContinue}
        >
          Continuar <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: First reading preview ───────────────────────────────────
function StepFirstReading({
  sunSign,
  onNext,
  onBack,
}: {
  sunSign: string;
  onNext: () => void;
  onBack: () => void;
}) {
  const { t }       = useI18n();
  const signEmoji   = getSignEmoji(sunSign);
  const signName    = getSignName(sunSign);
  const [text, setText]       = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    safeInvoke<{ text?: string; forecast?: string }>('forecast', {
      body: { period: 'daily' },
      fallback: {},
      timeout: 25_000,
    }).then(({ data }) => {
      const reading = data?.text ?? data?.forecast ?? '';
      setText(reading);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '0.75rem', textAlign: 'center' }}>
        {signEmoji || '⭐'}
      </span>
      <h2 className="font-display" style={{ fontSize: '1.375rem', color: 'var(--text-primary)', marginBottom: '0.4rem', textAlign: 'center' }}>
        {sunSign ? `Sua primeira leitura, ${signName}` : t('pages.onboarding.firstReadingTitle')}
      </h2>
      <p style={{ color: 'var(--text-tertiary)', marginBottom: '1.5rem', textAlign: 'center', fontSize: 'var(--text-caption)' }}>
        Gerado agora com base no seu mapa
      </p>

      <div className="card card-gold reading-container" style={{ marginBottom: '1.5rem', maxWidth: '100%' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Spinner size="md" />
          </div>
        ) : text ? (
          <div style={{ fontFamily: 'var(--font-reading)', fontSize: 'var(--text-reading)', lineHeight: 1.85, color: 'var(--text-primary)' }}>
            {text.split('\n').filter(Boolean).slice(0, 3).map((p, i) => (
              <p key={i} style={{ margin: '0 0 1rem' }}>{p}</p>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center' }}>
            {sunSign
              ? t('pages.onboarding.firstReadingDesc').replace('{sign}', signName)
              : t('pages.onboarding.firstReadingDesc2')}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button className="btn btn-secondary" onClick={onBack} style={{ flex: '0 0 auto' }}>
          {t('pages.onboarding.backBtn')}
        </button>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={onNext} disabled={loading}>
          Ver mapa completo → <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

const COMPARE_ROWS = [
  { feature: 'Mapa natal completo',     free: false, premium: true },
  { feature: 'Previsão diária',         free: true,  premium: true },
  { feature: 'Perguntas aos astros',    free: '3/mês', premium: 'Ilimitado' },
  { feature: 'Sinastria',               free: false, premium: true },
  { feature: 'Exportar PDF',            free: false, premium: true },
  { feature: 'Histórico completo',      free: false, premium: true },
];

// ─── Step 4: Upgrade prompt ──────────────────────────────────────────
function StepUpgrade({ onFinish, onSkip }: { onFinish: () => void; onSkip: () => void }) {
  return (
    <div>
      <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.75rem', textAlign: 'center' }}>✨</span>
      <h2 className="font-display" style={{ fontSize: '1.375rem', color: 'var(--text-primary)', marginBottom: '0.5rem', textAlign: 'center' }}>
        Quer ir mais fundo?
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.7, fontSize: 'var(--text-label)', textAlign: 'center' }}>
        O Premium desbloqueia tudo, incluindo seu mapa natal completo.
      </p>

      {/* Comparison table */}
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', background: 'var(--bg-elevated)', padding: '0.5rem 1rem', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>Funcionalidade</span>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', textAlign: 'center', minWidth: '56px' }}>Grátis</span>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 700, textAlign: 'center', minWidth: '72px' }}>Premium</span>
        </div>
        {COMPARE_ROWS.map(row => (
          <div key={row.feature} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', padding: '0.5rem 1rem', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>{row.feature}</span>
            <span style={{ textAlign: 'center', minWidth: '56px', fontSize: 'var(--text-caption)' }}>
              {row.free === true ? <Check size={14} style={{ color: '#4ade80' }} aria-label="Sim" /> : row.free === false ? <span style={{ color: 'var(--text-muted)' }}>-</span> : <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{row.free}</span>}
            </span>
            <span style={{ textAlign: 'center', minWidth: '72px', fontSize: 'var(--text-caption)', color: 'var(--petrol)' }}>
              {row.premium === true ? <Check size={14} style={{ color: 'var(--petrol)' }} aria-label="Sim" /> : <span style={{ fontSize: '11px' }}>{String(row.premium)}</span>}
            </span>
          </div>
        ))}
      </div>

      {/* 7-day guarantee seal */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1.25rem', padding: '0.625rem', background: 'rgba(31,78,95,0.05)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-gold)' }}>
        <ShieldCheck size={18} style={{ color: 'var(--petrol)', flexShrink: 0 }} aria-hidden />
        <span style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600 }}>Garantia de devolução de 7 dias · Sem perguntas</span>
      </div>

      <button className="btn btn-primary btn-block btn-lg" onClick={onFinish} style={{ marginBottom: '0.75rem' }}>
        Começar com Premium
      </button>
      <button
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 'var(--text-label)', width: '100%' }}
        onClick={onSkip}
      >
        Continuar grátis por enquanto
      </button>
    </div>
  );
}

// ─── Main Onboarding page ─────────────────────────────────────────────
export function OnboardingPage() {
  const navigate                = useNavigate();
  const { profile, refreshProfile } = useAuth();
  const { t }                   = useI18n();
  const [step,     setStep]     = useState(0);
  const [sunSign,  setSunSign]  = useState('');
  const [saving,   setSaving]   = useState(false);

  const TOTAL_STEPS = 5;  // 0:welcome 1:birth 2:quiz 3:preview 4:upgrade
  const firstName   = profile?.full_name?.split(' ')[0] ?? '';
  const skipSetupLabel = t('pages.onboarding.skipSetup');

  async function saveBirthData(data: { birth_date: string; birth_time: string; birth_city: string }, sign: string) {
    if (!profile) return;
    setSaving(true);
    await supabase.from('profiles').update({
      birth_date:  data.birth_date   || null,
      birth_time:  data.birth_time   || null,
      birth_city:  data.birth_city   || null,
      sun_sign:    sign              || null,
    }).eq('id', profile.id);
    await refreshProfile();
    setSaving(false);
  }

  async function saveQuiz(data: QuizData) {
    if (!profile) return;
    await supabase.from('onboarding_quiz_responses').upsert({
      user_id:    profile.id,
      purpose:    data.purpose,
      focus_area: data.focus_area,
      experience: data.experience,
    });
  }

  async function markDone() {
    if (!profile) return;
    await supabase.from('profiles').update({ onboarding_done: true }).eq('id', profile.id);
    // Upsert onboarding_progress
    await supabase.from('onboarding_progress').upsert({
      user_id:         profile.id,
      step:            TOTAL_STEPS,
      completed_steps: [0, 1, 2, 3, 4],
      completed_at:    new Date().toISOString(),
    });
  }

  async function handleFinish() {
    await markDone();
    navigate('/pricing', { replace: true });
  }

  async function handleSkip() {
    await markDone();
    navigate('/dashboard', { replace: true });
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-base)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1.5rem',
    }}>
      <div style={{ width: '100%', maxWidth: '480px' }}>

        {/* Progress */}
        <div style={{ marginBottom: '2.5rem' }}>
          <ProgressDots step={step} total={TOTAL_STEPS} />
        </div>

        {/* Step content */}
        <div className="card animate-fade-in">
          {saving && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Spinner size="lg" />
            </div>
          )}

          {!saving && step === 0 && (
            <StepWelcome name={firstName} onNext={() => setStep(1)} />
          )}

          {!saving && step === 1 && (
            <StepBirthData
              onBack={() => setStep(0)}
              onNext={async (data, sign) => {
                setSunSign(sign);
                await saveBirthData(data, sign);
                setStep(2);
              }}
            />
          )}

          {!saving && step === 2 && (
            <StepQuiz
              onBack={() => setStep(1)}
              onNext={async (data) => {
                await saveQuiz(data);
                setStep(3);
              }}
            />
          )}

          {!saving && step === 3 && (
            <StepFirstReading
              sunSign={sunSign || profile?.sun_sign || ''}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
            />
          )}

          {!saving && step === 4 && (
            <StepUpgrade onFinish={handleFinish} onSkip={handleSkip} />
          )}
        </div>

        {/* Skip only available from step 2 onward (steps 0 and 1 capture essential data) */}
        {step >= 2 && step < 4 && (
          <p style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}
              onClick={handleSkip}
            >
              {skipSetupLabel}
            </button>
          </p>
        )}

      </div>
    </div>
  );
}
